import { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { WifiOff, RefreshCw } from 'lucide-react';
import { AppRoutes } from './routes';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/Toast';
import { useSettingsStore } from './store/settingsStore';
import { usePricingStore } from './store/pricingStore';

const BOOTED_KEY = 'cuetitan_booted';

export default function App() {
  const { load: loadSettings } = useSettingsStore();
  const loadPricing = usePricingStore((s) => s.load);
  const [ready, setReady] = useState(false);
  const [connectError, setConnectError] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [retrying, setRetrying] = useState(false);
  const readyRef = useRef(false);

  const connect = useCallback(async () => {
    setConnectError(false);
    try {
      const res = await fetch('/api/license/status');
      const data = await res.json() as { status: string };
      if (data.status === 'active') {
        await Promise.all([loadSettings(), loadPricing()]);
        localStorage.setItem(BOOTED_KEY, '1');
        readyRef.current = true;
        setReady(true);
      }
    } catch {
      // If we've loaded successfully before, stay in the app (offline mode)
      if (localStorage.getItem(BOOTED_KEY) === '1') {
        readyRef.current = true;
        setReady(true);
        setOffline(true);
      } else {
        setConnectError(true);
      }
    }
  }, [loadSettings]);

  const retry = useCallback(async () => {
    setRetrying(true);
    await connect();
    setRetrying(false);
  }, [connect]);

  useEffect(() => { void connect(); }, [connect]);

  // Reload settings whenever the tab/screen becomes visible again so language
  // changes made by superadmin on another device propagate to all clients.
  // Debounced to avoid firing multiple requests on rapid tab switches.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onVisible = () => {
      if (!readyRef.current || document.visibilityState !== 'visible') return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void Promise.all([loadSettings(), loadPricing()]).catch(() => null); }, 200);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); if (timer) clearTimeout(timer); };
  }, [loadSettings, loadPricing]);

  useEffect(() => {
    const onOnline = () => {
      setOffline(false);
      // If app already loaded, just re-fetch settings silently
      if (readyRef.current) {
        void loadSettings().catch(() => null);
      } else {
        // Was blocked waiting for server — retry now
        void connect();
      }
    };
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [connect, loadSettings]);

  // Still booting
  if (!ready && !connectError) return null;

  // First-ever load and server unreachable
  if (!ready && connectError) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-red-950/40 border border-red-900/40
                          flex items-center justify-center mx-auto mb-5">
            <WifiOff size={28} className="text-red-400" strokeWidth={1.5} />
          </div>
          <p className="text-white text-lg font-semibold mb-1.5">Cannot reach server</p>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            {offline
              ? 'Your device is offline. Connect to the internet and try again.'
              : 'The server may still be starting up. Wait a moment and retry.'}
          </p>
          <button
            onClick={retry}
            disabled={retrying}
            className="px-6 py-2.5 bg-felt-700 hover:bg-felt-600 text-white rounded-xl
                       text-sm font-medium transition-colors flex items-center gap-2 mx-auto
                       disabled:opacity-50"
          >
            <RefreshCw size={14} className={retrying ? 'animate-spin' : ''} strokeWidth={2} />
            {retrying ? 'Connecting…' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {offline && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-2.5
                        bg-gray-900/95 backdrop-blur-sm border-b border-gray-800
                        px-4 py-2 shadow-lg">
          <WifiOff size={13} className="text-gray-500 shrink-0" strokeWidth={2} />
          <p className="text-gray-500 text-xs font-medium flex-1">
            Offline — will reconnect automatically
          </p>
        </div>
      )}
      <div className={offline ? 'pt-9' : undefined}>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </div>
      <ToastContainer />
    </BrowserRouter>
  );
}
